// DTO ⇄ form values for Receive shipment — the only place its field names live.
import { formatTextOrDash } from '@/lib/format/text';
import { unitSymbol } from '@/lib/mappers/units';
import { formValue, appendIfPresent } from '@/lib/mappers/formValues';
import { appendFormAttachments } from '@/lib/mappers/attachments';
import { joinReferenceId } from '@/lib/utils/id';
import { CONTRACT_STATUS } from '@/config/statuses';
import {
  FACILITY_KINDS,
  buildFacilityValue,
  toAllocationPreferenceArray,
} from '@/config/formOptions';
import { workingUnitOf } from './contractsMappers';

// Every field the form owns. A missing key here would make its input uncontrolled.
export const EMPTY_RECEIVE_SHIPMENT_FORM = Object.freeze({
  idPrefix: '',
  idNumber: '',
  // Not asked for — inherited from the contract — but <TerminalField> reads the site off it.
  facilityId: '',
  dateOfDispatch: '',
  dateOfDischarge: '',
  terminalId: '',
  transportName: '',
  vessel: '',
  shipmentQuantity: '',
  shipmentQuantityUnit: '',
  // Cross-category conversions only: declaration↔working and declaration↔tracking.
  workingDensity: '',
  trackingUnit: '',
  trackingDensity: '',
  ghgEmissions: '',
  ghgEmissionSavings: '',
  rawMaterialType: '',
  countryOfOrigin: '',
  feedstockCategory: '',
  allocationPreference: [],
  attachments: [],
});

/**
 * One `contract(id)` node → the Receive shipment screen: the read-only contract bar and the
 * form values the new shipment inherits, its quantity prefilled from the contract's own.
 */
export const processContractForReceiveShipment = (contract) => {
  // Not `contract.workingUnit` — a contract created without a factor stored none.
  const workingUnit = unitSymbol(workingUnitOf(contract));
  const declarationUnit = unitSymbol(contract?.declarationUnit);

  return {
    id: contract?.id,
    contractId: formatTextOrDash(contract?.contractId),
    isClosed: contract?.status === CONTRACT_STATUS.closed,
    materialName: formatTextOrDash(contract?.material?.name),
    facilityName: formatTextOrDash(
      contract?.site?.siteName ?? contract?.backToBackAccount?.accountName,
    ),
    supplierName: formatTextOrDash(contract?.supplierAccount?.companyname),
    // A back-to-back facility has no terminals, and the server nulls the column for one.
    isBackToBack: contract?.backToBackAccount?.id != null,
    // The server always applies a contract's terminal (id or legacy text) and 400s on another.
    terminalLocked: contract?.terminalRefId != null || Boolean(contract?.terminal?.trim()),
    siteId: contract?.site?.id ? String(contract.site.id) : '',
    workingUnit,
    declarationUnit,
    // The read-only columns convert THIS, not the entry: the amount the contract is for.
    contractedQuantity: formValue(contract?.contractedQuantity),
    // GraphQL sends this one unmapped, so it must be symbolised to match the units.
    contractedQuantityUnit: unitSymbol(contract?.contractedQuantityUnit),
    // What <ShipmentQuantityFields> reads where there is no Material dropdown to read.
    material: {
      declarationUnit,
      workingUnit,
      density:
        contract?.conversionFactor ??
        contract?.material?.displayUnits?.[0]?.convertedWeightQuantity ??
        null,
    },
    values: {
      ...EMPTY_RECEIVE_SHIPMENT_FORM,
      facilityId: contract?.site?.id
        ? buildFacilityValue(FACILITY_KINDS.site, contract.site.id)
        : contract?.backToBackAccount?.id
          ? buildFacilityValue(FACILITY_KINDS.backToBack, contract.backToBackAccount.id)
          : '',
      terminalId: contract?.terminalRefId ? String(contract.terminalRefId) : '',
      transportName: contract?.transport ?? '',
      vessel: contract?.vessel ?? '',
      workingDensity: formValue(contract?.conversionFactor),
      ghgEmissions: formValue(contract?.ghgEmissions),
      ghgEmissionSavings: formValue(contract?.ghgEmissionSavings),
      rawMaterialType: contract?.rawMaterialType ?? '',
      countryOfOrigin: contract?.countryOfOrigin ?? '',
      feedstockCategory: contract?.feedstockCategory ?? '',
      allocationPreference: toAllocationPreferenceArray(contract?.preferredAllocation),
    },
  };
};

export const buildReceiveShipmentDto = (values) => ({
  shipmentId: joinReferenceId(values),
  shipmentQuantity: values.shipmentQuantity,
  shipmentUnit: values.shipmentQuantityUnit,
  terminalId: values.terminalId,
  dateOfDispatch: values.dateOfDispatch,
  // Unlike the fields below, an empty `densityTracking` is rejected — so it is omitted.
  densityTracking: values.trackingUnit ? values.trackingDensity : '',
});

/**
 * Fields the controller inherits from the CONTRACT when the key is absent, so a blank one
 * must travel as an empty string — the server's only "clear".
 */
export const buildInheritedReceiveFields = (values) => ({
  dateOfDischarge: values.dateOfDischarge,
  transport: values.transportName,
  vessel: values.vessel,
  density: values.workingDensity,
  trackingUnit: values.trackingUnit,
  ghgEmissions: values.ghgEmissions,
  ghgEmissionSavings: values.ghgEmissionSavings,
  rawMaterialType: values.rawMaterialType,
  countryOfOrigin: values.countryOfOrigin,
  feedstockCategory: values.feedstockCategory,
});

export const buildReceiveShipmentFormData = (values) => {
  const formData = new FormData();
  const dto = buildReceiveShipmentDto(values);
  const inherited = buildInheritedReceiveFields(values);

  Object.keys(dto).forEach((key) => appendIfPresent(formData, key, dto[key]));
  Object.keys(inherited).forEach((key) => formData.append(key, inherited[key] ?? ''));

  // Repeated parts, not one JSON string — that is how multer builds a real array.
  (values.allocationPreference ?? []).forEach((allocation) =>
    formData.append('preferredAllocation', allocation),
  );

  appendFormAttachments(formData, values);

  return formData;
};
